// How a work item is described wherever it is listed: its icon, a title with
// the party, and the facts behind it — all from the item's own `summary`
// (GET /work), never from a guess about what an item of that kind usually is.

import type { WorkItem } from '../api/types.ts';
import { DOCUMENT_STATES, MATCH_STATES, OPERATION_STATES, PROPOSAL_STATES, type StateDisplay } from '../states.ts';
import type { IconName } from './Icon.tsx';

export const KIND_LABELS: Readonly<Record<WorkItem['kind'], string>> = {
  document: 'Document',
  proposal: 'Booking proposal',
  operation: 'Operation',
  bank_match: 'Bank match',
  request: 'Customer request',
};

/** Which state table describes a work item of this kind. */
export function workTable(kind: WorkItem['kind']): Readonly<Record<string, StateDisplay>> {
  switch (kind) {
    case 'document':
      return DOCUMENT_STATES;
    case 'proposal':
      return PROPOSAL_STATES;
    case 'operation':
      return OPERATION_STATES;
    case 'bank_match':
      return MATCH_STATES;
    default:
      return {};
  }
}

export function rowIcon(item: WorkItem): { name: IconName; tone: string } {
  if (item.kind === 'bank_match') return { name: 'swap', tone: 'bl' };
  if (item.kind === 'operation') return { name: 'bolt', tone: 'ink' };
  if (item.kind === 'request') return { name: 'mail', tone: 'ink' };
  const image = item.summary?.mediaType?.startsWith('image/') === true;
  const needsYou = item.state === 'needs_review' || item.state === 'quarantined';
  return { name: image ? 'camera' : 'file', tone: needsYou ? 'am' : 'gr' };
}

export const DOCUMENT_TYPE_LABELS: Readonly<Record<string, string>> = {
  invoice: 'Invoice',
  credit_note: 'Credit note',
  receipt: 'Receipt',
  bank_statement: 'Bank statement',
};


/** "Booking proposal · KPN Zakelijk", "Bank match · Test Supplier BV". */
export function itemTitle(item: WorkItem): string {
  const party = item.summary?.supplierName ?? null;
  const base =
    item.kind === 'document'
      ? (DOCUMENT_TYPE_LABELS[item.summary?.documentType ?? item.detail ?? ''] ?? item.detail ?? 'Document')
      : KIND_LABELS[item.kind];
  return party === null ? base : `${base} · ${party}`;
}

/** The invoice number, and for a bank match what it would do. */
export function itemFacts(item: WorkItem): string[] {
  const invoice = item.summary?.invoiceNumber ?? null;
  return [invoice, item.kind === 'bank_match' && invoice !== null ? 'would settle this invoice' : null].filter(
    (part): part is string => part !== null,
  );
}

/** The document a row opens, if it has one. */
export function documentOf(item: WorkItem): string | null {
  return item.kind === 'document' ? item.id : (item.summary?.documentId ?? null);
}
